import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Search, Filter, Download, Eye, Mail, Phone, 
  Briefcase, Calendar, FileText, CheckCircle, Clock, XCircle,
  ChevronDown, ExternalLink, Loader2, User, Building2, LogOut
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700', icon: Clock },
  reviewing: { label: 'Reviewing', color: 'bg-yellow-100 text-yellow-700', icon: Eye },
  interviewed: { label: 'Interviewed', color: 'bg-purple-100 text-purple-700', icon: User },
  offered: { label: 'Offered', color: 'bg-green-100 text-green-700', icon: FileText },
  hired: { label: 'Hired', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const BackOfficeApplications = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('backoffice_token');
    if (!token) {
      navigate('/backoffice/login');
      return;
    }
    fetchApplications();
  }, [navigate, statusFilter]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('backoffice_token');
      const statusQuery = statusFilter !== 'all' ? `&status=${statusFilter}` : '';
      
      const response = await fetch(`${API_URL}/api/admin/applications?limit=100${statusQuery}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 401) {
          navigate('/backoffice/login');
          return;
        }
        throw new Error('Failed to fetch applications');
      }

      const data = await response.json();
      setApplications(data.applications);
      setTotal(data.total);
    } catch (err) {
      console.error('Error:', err);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (applicationId, newStatus) => {
    try {
      setUpdatingStatus(applicationId);
      const token = localStorage.getItem('backoffice_token');
      
      const response = await fetch(`${API_URL}/api/admin/applications/${applicationId}/status`, {
        method: 'PATCH',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to update status');

      // Update local state
      setApplications(apps => 
        apps.map(app => 
          app.id === applicationId ? { ...app, status: newStatus } : app
        )
      );
      
      toast.success(`Status updated to ${statusConfig[newStatus].label}`);
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('backoffice_token');
    localStorage.removeItem('backoffice_user');
    toast.success('Logged out successfully');
    navigate('/backoffice/login');
  };

  const filteredApplications = applications.filter(app => {
    const matchesSearch = 
      app.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.job_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.company?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const StatusBadge = ({ status }) => {
    const config = statusConfig[status] || statusConfig.new;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#0A1F44] text-white py-4 px-6 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/backoffice')}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            <div className="h-6 w-px bg-white/20" />
            <h1 className="text-xl font-bold">Job Applications</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-300">
              {total} total application{total !== 1 ? 's' : ''}
            </div>
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-white/10 rounded-lg"
              data-testid="applications-logout-button"
              title="Sign Out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 py-4 px-6 sticky top-[64px] z-40">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, email, job title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="applications-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
              <Filter className="h-4 w-4 mr-2 text-gray-400" />
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(statusConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-[#D4AF37] animate-spin" />
          </div>
        ) : filteredApplications.length === 0 ? (
          <Card className="border-none shadow-lg">
            <CardContent className="p-12 text-center">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-700 mb-2">No Applications Found</h3>
              <p className="text-gray-500">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your filters'
                  : 'Applications will appear here when candidates apply for jobs'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredApplications.map((app) => (
              <Card 
                key={app.id} 
                className="border-none shadow-md hover:shadow-lg transition-shadow"
                data-testid={`application-card-${app.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                    {/* Applicant Info */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-xl font-bold text-[#0A1F44]">{app.full_name}</h3>
                          <p className="text-gray-500 text-sm">Applied {formatDate(app.submitted_at)}</p>
                        </div>
                        <StatusBadge status={app.status} />
                      </div>

                      {/* Job Info */}
                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <div className="flex items-center gap-2 text-[#0A1F44] font-medium mb-1">
                          <Briefcase className="h-4 w-4 text-[#D4AF37]" />
                          {app.job_title}
                        </div>
                        <div className="flex items-center gap-2 text-gray-600 text-sm">
                          <Building2 className="h-4 w-4 text-gray-400" />
                          {app.company}
                        </div>
                      </div>

                      {/* Contact Info */}
                      <div className="flex flex-wrap gap-4 text-sm">
                        <a 
                          href={`mailto:${app.email}`}
                          className="flex items-center gap-2 text-gray-600 hover:text-[#D4AF37] transition-colors"
                        >
                          <Mail className="h-4 w-4" />
                          {app.email}
                        </a>
                        <a 
                          href={`tel:${app.phone}`}
                          className="flex items-center gap-2 text-gray-600 hover:text-[#D4AF37] transition-colors"
                        >
                          <Phone className="h-4 w-4" />
                          {app.phone}
                        </a>
                        {app.linkedin_url && (
                          <a 
                            href={app.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-gray-600 hover:text-[#D4AF37] transition-colors"
                          >
                            <ExternalLink className="h-4 w-4" />
                            LinkedIn
                          </a>
                        )}
                      </div>

                      {/* Cover Letter Preview */}
                      {app.cover_letter && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-gray-700 line-clamp-2">
                            <span className="font-medium text-blue-700">Note:</span> {app.cover_letter}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-3 lg:w-48">
                      {/* Resume Download */}
                      {app.resume_file && (
                        <Button
                          variant="outline"
                          className="w-full border-[#0A1F44] text-[#0A1F44] hover:bg-[#0A1F44] hover:text-white"
                          onClick={() => window.open(`${API_URL}/uploads/${app.resume_file}`, '_blank')}
                          data-testid={`download-resume-${app.id}`}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Resume
                        </Button>
                      )}

                      {/* Status Update */}
                      <Select 
                        value={app.status} 
                        onValueChange={(value) => updateStatus(app.id, value)}
                        disabled={updatingStatus === app.id}
                      >
                        <SelectTrigger 
                          className="w-full"
                          data-testid={`status-select-${app.id}`}
                        >
                          {updatingStatus === app.id ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <ChevronDown className="h-4 w-4 mr-2" />
                          )}
                          <span>Update Status</span>
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(statusConfig).map(([key, config]) => (
                            <SelectItem key={key} value={key}>
                              <span className="flex items-center gap-2">
                                <config.icon className="h-3 w-3" />
                                {config.label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      {/* View in ATS */}
                      {app.ats_candidate_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-500 hover:text-[#D4AF37]"
                          onClick={() => window.open('https://ats-central-2.emergent.host', '_blank')}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          View in ATS
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default BackOfficeApplications;
